import type { AxiosError } from 'axios'

import { toast } from '@utils/functions/notificationToast'
import { useOcNfModal } from '.'
import { parseItemPurchaseOrderObservationPayload } from '../helpers/parseItemPurchaseOrderObservationPayload'
import { parseItemsPurchaseOrdersPayload } from '../helpers/parseItemsPurchaseOrdersPayload'
import { parsePurchaseOrdersPayload } from '../helpers/parsePurchaseOrdersPayload'
import { parseTransferReversePurchaseOrderPayload } from '../helpers/parseTransferReversePurchaseOrderPayload'
import { useConsumption } from '@pages/Consumption/context'

import type {
  ItemsPurchaseOrdersFormDataProps,
  PurchaseOrdersFormDataProps,
  TransferReversePurchaseOrderFormProps
} from './types'
import type { PurchaseOrderDetailsFormProps } from '../components/PurchaseOrderDetails/form/types'

import * as apiOcNf from '@services/api/ocNf'

const reversePurchaseOrder = async (formData: TransferReversePurchaseOrderFormProps) => {
  const { setIsReversePurchaseOrderLoading } = useOcNfModal.getState()
  const { getBigNumbersConsumption, getDetailedSummary, getWeeklyBreakdown, getClassifiedConsumption } = useConsumption.getState()

  setIsReversePurchaseOrderLoading(true)

  try {
    const payload = parseTransferReversePurchaseOrderPayload(formData)

    await apiOcNf.reversePurchaseOrder(payload)

    toast.success('Ordem de compra revertida com sucesso!')

    getPurchaseOrders({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      supplierCode: formData.supplierCode,
      itemClassification: formData.itemClassification
    })

    getBigNumbersConsumption({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getClassifiedConsumption({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getWeeklyBreakdown({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Falha ao reverter a ordem de compra!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReversePurchaseOrderLoading(false)
  }
}

const transferPurchaseOrder = async (formData: TransferReversePurchaseOrderFormProps) => {
  const { setIsTransferPurchaseOrderLoading } = useOcNfModal.getState()
  const { getBigNumbersConsumption, getDetailedSummary, getWeeklyBreakdown, getClassifiedConsumption } = useConsumption.getState()

  setIsTransferPurchaseOrderLoading(true)

  try {
    const payload = parseTransferReversePurchaseOrderPayload(formData)

    await apiOcNf.transferPurchaseOrder(payload)

    toast.success('Ordem de compra transferida com sucesso!')

    getPurchaseOrders({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      supplierCode: formData.supplierCode,
      itemClassification: formData.itemClassification
    })

    getBigNumbersConsumption({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getClassifiedConsumption({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getWeeklyBreakdown({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Falha ao transferir a ordem de compra!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsTransferPurchaseOrderLoading(false)
  }
}

const editItemPurchaseOrderObservation = async (formData: PurchaseOrderDetailsFormProps) => {
  const { setIsEditItemPurchaseOrderObservationLoading } = useOcNfModal.getState()

  setIsEditItemPurchaseOrderObservationLoading(true)

  try {
    const payload = parseItemPurchaseOrderObservationPayload(formData)

    await apiOcNf.editItemPurchaseOrderObservation(payload)

    toast.success('Observação da ordem de compra foi editada com sucesso!')

    getPurchaseOrders({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      supplierCode: formData.supplierCode,
      itemClassification: formData.itemClassification
    })
  } catch (error) {
    toast.error('Falha ao editar a observação da ordem de compra!', (error as AxiosError)?.status)
  } finally {
    setIsEditItemPurchaseOrderObservationLoading(false)
  }
}

const getItemsPurchaseOrders = async (formData: ItemsPurchaseOrdersFormDataProps | null) => {
  const { setItemsPurchaseOrdersData, setIsItemsPurchaseOrdersLoading } = useOcNfModal.getState()

  setIsItemsPurchaseOrdersLoading(true)

  try {
    const payload = parseItemsPurchaseOrdersPayload(formData)

    const data = await apiOcNf.getItemsPurchaseOrders(payload)

    setItemsPurchaseOrdersData(data)
  } catch (error) {
    toast.error('Falha ao buscar os itens das ordens de compra!', (error as AxiosError)?.status)
  } finally {
    setIsItemsPurchaseOrdersLoading(false)
  }
}

const getPurchaseOrders = async (formData: PurchaseOrdersFormDataProps | null) => {
  const { setPurchaseOrdersData, setIsPurchaseOrdersLoading } = useOcNfModal.getState()

  setIsPurchaseOrdersLoading(true)

  try {
    const payload = parsePurchaseOrdersPayload(formData)

    const data = await apiOcNf.getPurchaseOrders(payload)

    setPurchaseOrdersData(data)
  } catch (error) {
    toast.error('Falha ao buscar as ordens de compra!', (error as AxiosError)?.status)
  } finally {
    setIsPurchaseOrdersLoading(false)
  }
}

export {
  getPurchaseOrders,
  getItemsPurchaseOrders,
  editItemPurchaseOrderObservation,
  reversePurchaseOrder,
  transferPurchaseOrder
}
